import { combineReducers, Store as ngrxStore } from '@ngrx/store';
import { compose } from '@ngrx/core/compose';
import { storeFreeze } from 'ngrx-store-freeze';

import { RootModel, RootModelLists, RootModelObjects } from './root.model';

import { messageReducer } from './message/message.reducer';

import {
  navigationNotificationsReducer,
  navigationProjectFeedFailingContestsReducer,
  navigationProjectFeedFailingProjectsReducer,
  navigationProjectFeedReducer,
  navigationUserBalancesReducer,
  navigationUserInfoReducer,
  navigationUserRecentProjectsAndContestsReducer,
} from './navigation/navigation.reducer';
import { giveGetEligibilityReducer } from './give-get/give-get.reducer';

import { onlineOfflineReducer } from './online-offline/online-offline.reducer';

import {
  threadReducer,
  threadUnreadCountReducer,
} from './thread/thread.reducer';

import { notificationsUserPreferencesReducer } from './notification-preferences/notification-preferences.reducer';

import { WebsocketBaseEvent } from './websocket';

/* Wrap ngrx's Store as our own Store that only takes our action types. */
export class Store<S> extends ngrxStore<S> {
  dispatch<T extends keyof RootModel>(action: Action<T>): void {
    super.dispatch(action);
  }
  next<T extends keyof RootModel>(action: Action<T>): void {
    super.next(action);
  }
}

export interface Reference<T extends keyof RootModel> {
  path: Path<T>;
  query?: Query;
}

export type Path<T extends keyof RootModel> = [T, string];

// Queries that you can pass to an endpoint.
export interface Query {
  limit?: number;
  offset?: number;
}

export interface Pagination {
  limit?: number;
  offset?: number;
}

export type Action<T extends keyof RootModel> =
  | { type: 'API_FETCH_SUCCESS'; payload?: ApiActionPayload<T> }
  | { type: 'API_POST'; payload?: ApiActionPayload<T> }
  | { type: 'API_POST_SUCCESS'; payload?: ApiActionPayload<T> }
  | { type: 'WS_MESSAGE'; payload: WebsocketActionPayload };

export type ActionType =
  | 'API_FETCH_SUCCESS'
  | 'API_POST'
  | 'API_POST_SUCCESS'
  | 'WS_MESSAGE';

export interface ApiActionPayload<T extends keyof RootModel> {
  ref: Reference<T>;
  data: any;
}

export type WebsocketActionPayload = WebsocketBaseEvent & { userId: string };

export interface Entities<T> {
  [id: string]: T;
}

export interface Lists<T> {
  [query: string]: string[];
}

export interface EntityList<T> {
  entities?: Entities<T>;
  lists?: Lists<T>;
}
export type StateSlice<T> = { [P in keyof T]: EntityList<T[P]> };

export type ListReducer<T extends keyof RootModelLists> = (
  state: StateSlice<RootModelLists[T]>,
  action: Action<T>,
) => StateSlice<RootModelLists[T]>;

export type ObjectReducer<T extends keyof RootModelObjects> = (
  state: RootModelObjects[T],
  action: Action<T>,
) => RootModelObjects[T];

export type Reducer<S> = (state: S, action: Action<any>) => S;

type RootModelToNgrxStoreState<T> = {
  [P in keyof T]: { [Q in keyof T[P]]: EntityList<T[P][Q]> }
};

type RootModelToNgrxStoreEntities<
  T,
  P extends keyof T,
  Q extends keyof T[P]
> = Entities<T[P][Q]>;

export type ListState = RootModelToNgrxStoreState<RootModelLists>;
export type ObjectState = RootModelObjects;
export type State = ListState & ObjectState;

function compare<T>(a: T, b: T) {
  return a < b ? -1 : a > b ? 1 : 0;
}

function arrayMerge<T>(a: T[], b: T[]) {
  const result = [];
  const max = Math.max(a.length, b.length);
  for (let i = 0; i < max; i++) {
    if (a[i] || b[i]) {
      result[i] = a[i] || b[i];
    }
  }
  return result;
}

function arrayShift<T>(a: T[], offset: number) {
  return [...new Array<T>(offset), ...a];
}

/* This merges a number of entities into the state.
 * If an offset it specified then the new entities are placed at that offset.
 * If an offset is not specified then the new entities will be:
 *   - merged if they have the same id as an existing entity OR
 *   - placed at the start of the list (and the offset of existing items moved up)
 *
 * We also allow a custom sorting function to allow resorting the list.
 * If this is used all the offsets will be then be reindexed from 0.
 */
export function mergeEntities<T extends keyof RootModelLists>(
  state: ListState[T],
  entities: RootModelToNgrxStoreEntities<RootModelLists, T, string>,
  ref: Reference<T>,
  sortKey?: keyof RootModelLists[T][string],
): ListState[T] {
  const data = state[ref.path[1]] || { entities: {} };
  const query = 'default'; // FIXME
  const originalList = data.lists ? [...data.lists[query]] || [] : [];

  const updatedEntities = {
    ...data.entities,
    ...entities,
  };

  const newEntityIds = Object.keys(updatedEntities).filter(
    key => !Object.keys(data.entities).includes(key),
  );

  // If the new list doesn't have an offset then the new list is put at the front.
  const originalListOffset =
    ref.query && ref.query.offset ? 0 : newEntityIds.length;
  const newListOffset = (ref.query && ref.query.offset) || 0;

  const updatedQueryList = arrayMerge(
    arrayShift(originalList, originalListOffset),
    arrayShift(newEntityIds, newListOffset),
  );

  if (sortKey) {
    updatedQueryList.sort((a, b) =>
      compare(updatedEntities[a][sortKey], updatedEntities[b][sortKey]),
    );
  }
  return Object.assign({}, state, {
    [ref.path[1]]: {
      entities: updatedEntities,
      lists: {
        ...data.lists,
        ...{ [query]: updatedQueryList },
      },
    },
  });
}

export function mergeEntity<T extends keyof RootModelObjects>(
  state: ObjectState[T],
  entity: RootModelObjects[T]['string'],
  path: Path<T>,
): ObjectState[T] {
  return Object.assign({}, state, { [path[1]]: entity });
}

export function store(state: any, action: any) {
  const reducers: { [k in keyof RootModel]: Reducer<any> } = {
    giveGetEligibility: giveGetEligibilityReducer,
    messages: messageReducer,
    navigationNotifications: navigationNotificationsReducer,
    navigationProjectFeed: navigationProjectFeedReducer,
    navigationProjectFeedFailingContests: navigationProjectFeedFailingContestsReducer,
    navigationProjectFeedFailingProjects: navigationProjectFeedFailingProjectsReducer,
    navigationUserBalances: navigationUserBalancesReducer,
    navigationUserInfo: navigationUserInfoReducer,
    navigationUserRecentProjectsAndContests: navigationUserRecentProjectsAndContestsReducer,
    notificationsUserPreferences: notificationsUserPreferencesReducer,
    onlineOffline: onlineOfflineReducer,
    threads: threadReducer,
    threadUnreadCount: threadUnreadCountReducer,
  };

  const reducer = compose(storeFreeze, combineReducers)(reducers);
  return reducer(state, action);
}



// WEBPACK FOOTER //
// /mnt/jenkins-slave-workspace/workspace/gaf-build/webapp/src/app/@shared/datastore/store.ts